/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { initPageable, NestedErrorMessage } from "@advapacs/common";
import {
  ICustomerPACS,
  defaultValue,
  initCounterPACS,
  PaginationPacsResponse,
  CounterPACS,
} from "../../../types/customerPACS";
const initialState = {
  loading: false,
  disclaimerErrorMessage: null as NestedErrorMessage | null,
  errorMessage: null as NestedErrorMessage | null,
  entities: [] as Array<ICustomerPACS>,
  entity: defaultValue,
  isUpdated: false,
  updatedPassword: false,
  pageable: initPageable,
  counterPacs: initCounterPACS,
  showFilter: false,
  filter: {
    status: undefined,
    category: undefined,
    searchKeyword: undefined as string | undefined,
  },
  requested: false,
};
const customerPACSSlice = createSlice({
  name: "customerPACSSlice",
  initialState,
  reducers: {
    startSearchCustomerPACS(state) {
      state.loading = true;
      state.entities = [];
    },
    failSearchCustomerPACS(
      state,
      action: PayloadAction<NestedErrorMessage | null>
    ) {
      state.loading = false;
      state.entities = [];
      state.errorMessage = action.payload;
    },
    searchCustomerPACSSuccess(
      state,
      action: PayloadAction<PaginationPacsResponse<ICustomerPACS>>
    ) {
      state.loading = false;
      state.entities = action.payload?.content;
      state.pageable = action.payload?.pageable;
      state.counterPacs = action.payload?.count as unknown as CounterPACS;
    },
    getCustomerPACSDetailStart(state) {
      state.loading = false;
      state.isUpdated = false;
      state.errorMessage = null;
    },
    getCustomerPACSDetailSuccess(state, action: PayloadAction<ICustomerPACS>) {
      state.loading = false;
      state.entity = { ...action.payload };
      state.isUpdated = false;
    },
    getCustomerPACSDetailFail(
      state,
      action: PayloadAction<NestedErrorMessage | null>
    ) {
      state.loading = false;
      state.errorMessage = action.payload;
    },
    requestAccessStart: (state) => {
      state.loading = true;
      state.requested = false;
    },
    requestAccessSuccess: (state) => {
      state.loading = false;
      state.requested = true;
    },
    requestAccessFail: (
      state,
      action: PayloadAction<NestedErrorMessage | null>
    ) => {
      state.loading = false;
      state.errorMessage = action.payload;
    },
    getDisclaimerFail: (
      state,
      action: PayloadAction<NestedErrorMessage | null>
    ) => {
      state.loading = false;
      state.disclaimerErrorMessage = action.payload;
    },
    clearData(state) {
      state.entities = [];
      state.entity = defaultValue;
      state.loading = false;
      state.isUpdated = false;
      state.errorMessage = null;
      state.filter = {
        status: undefined,
        category: undefined,
        searchKeyword: undefined as string | undefined,
      };
    },
    clearErrorMessage(state) {
      state.errorMessage = null;
      state.disclaimerErrorMessage = null;
    },
    setShowFilter(state) {
      state.showFilter = !state.showFilter;
    },
    setFilterChange(
      state,
      action: PayloadAction<{
        category?: string | undefined;
        searchKeyword?: string | undefined;
        status?: string | undefined;
      }>
    ) {
      // @ts-ignore
      state.filter = action.payload;
    },
    resetFilter(state) {
      state.filter = initialState.filter;
    },
    setErrorMessage(state, action) {
      state.errorMessage = action.payload;
    },
  },
});

export const CustomerPACSAction = customerPACSSlice.actions;
// @ts-ignore
export default customerPACSSlice.reducer;
